import React, { createContext, useContext, useState, useCallback } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { OrganizationInfoInitialValues } from 'pages/FreeTrialActivation/components/OrganizationInfo';
import { ResultItem } from 'pages/Authentication/types/AddressSearchAutoComplete';


interface AddressForm {
    street?: string;
    state?: string;
    zip?: string;
    city?: string;
    streetAdditional?: string;
    fieldValue?: ResultItem | null;
    country?: string;
  }

interface FreeTrialOnboardingContextType {
  orgInfo: {
    form: {
      practiceName: string;
      address: AddressForm;
      designation: string;
      npi: string;
      patients: string;
      designationDesc?: string | undefined;
    };
    practiceSearch: string;
  };
  setOrgInfo: React.Dispatch<React.SetStateAction<{
    form: {
      practiceName: string;
      address: AddressForm;
      designation: string;
      npi: string;
      patients: string;
      designationDesc?: string | undefined;
    };
    practiceSearch: string;
  }>>;
  createPassword: {
    password: string;
    error: string;
  };
  setCreatePassword: React.Dispatch<React.SetStateAction<{
    password: string;
    error: string;
  }>>;
  verifyEmail: {
    code: string;
    resendTimer: number;
  };
  setVerifyEmail: React.Dispatch<React.SetStateAction<{
    code: string;
    resendTimer: number;
  }>>;
  fields: {
    firstName: string;
    lastName: string;
    email: string;
    checked: boolean;
  };
  setFields: React.Dispatch<React.SetStateAction<{
    firstName: string;
    lastName: string;
    email: string;
    checked: boolean;
  }>>;
  step: number;
  setStep: (step: number) => void;
  goBack: () => void;
  emailMasked: string;
  setEmailMasked: (email: string) => void;
  providerId?: string;
  setProviderId: (id?: string) => void;
  token?: string;
  setToken: Dispatch<SetStateAction<string | undefined>>;
  skipPassword: boolean;
  setSkipPassword: (skip: boolean) => void;
  pendingOrgStep: boolean;
  setPendingOrgStep: (pending: boolean) => void;
  orgInitialValues: OrganizationInfoInitialValues | null;
  setOrgInitialValues: (vals: OrganizationInfoInitialValues | null) => void;
  totalSteps: number;
  handleSilentLogin: (accessToken?: string) => void;
  stepHistory: number[];
  onboardingStatus: string;
  setOnboardingStatus: React.Dispatch<React.SetStateAction<string>>;
}


const FreeTrialOnboardingContext = createContext<
  FreeTrialOnboardingContextType | undefined
>(undefined);


export function useFreeTrialOnboarding() {
  const ctx = useContext(FreeTrialOnboardingContext);
  if (!ctx) throw new Error('useFreeTrialOnboarding must be used within FreeTrialOnboardingProvider');
  return ctx;
}


export function FreeTrialOnboardingProvider({ children }: { children: React.ReactNode }) {
  const [step, setStepState] = useState(0);
  const [verifyEmail, setVerifyEmail] = useState({
    code: '',
    resendTimer: 0,
  });
  const [orgInfo, setOrgInfo] = useState({
    form: {
      practiceName: '',
      address: {},
      designation: '',
      npi: '',
      patients: '',
    },
    practiceSearch: '',
  });
  const [createPassword, setCreatePassword] = useState({
    password: '',
    error: '',
  });
  const [fields, setFields] = useState({
    firstName: '',
    lastName: '',
    email: '',
    checked: false,
  });
  const [stepHistory, setStepHistory] = useState<number[]>([]);
  const [emailMasked, setEmailMasked] = useState('c*****@e****.com');
  const [providerId, setProviderId] = useState<string | undefined>(undefined);
  const [token, setToken] = useState<string | undefined>(undefined);
  const [skipPassword, setSkipPassword] = useState(false);
  const [pendingOrgStep, setPendingOrgStep] = useState(false);
  const [orgInitialValues, setOrgInitialValues] = useState<
    OrganizationInfoInitialValues | null
  >(null);
  const totalSteps = skipPassword ? 5 : 6;
  const [onboardingStatus, setOnboardingStatus] = useState('');

  const setStep = useCallback((nextStep: number) => {
    setStepHistory((h) => [...h, step]);
    setStepState(nextStep);
  }, [step]);

  const goBack = useCallback(() => {
    setStepHistory((h) => {
      if (h.length === 0) return h;
      const prev = h[h.length - 1];
      setStepState(prev);
      return h.slice(0, -1);
    });
  }, []);

  const handleSilentLogin = useCallback((accessToken?: string) => {
    setTimeout(() => {
      if (accessToken) {
        localStorage.setItem('accessToken', accessToken);
      }
      // Set onboardingModalSeen to a temporary value so Dashboard knows to show modal once
      localStorage.setItem('onboardingModalSeen', 'just-completed');
      window.location.assign('/dashboard');
    }, 0);
  }, []);

  const contextValue = React.useMemo(() => ({
    orgInfo,
    setOrgInfo,
    createPassword,
    setCreatePassword,
    verifyEmail,
    setVerifyEmail,
    fields,
    setFields,
    step,
    setStep,
    goBack,
    emailMasked,
    setEmailMasked,
    providerId,
    setProviderId,
    token,
    setToken,
    skipPassword,
    setSkipPassword,
    pendingOrgStep,
    setPendingOrgStep,
    orgInitialValues,
    setOrgInitialValues,
    totalSteps,
    handleSilentLogin,
    stepHistory,
    onboardingStatus,
    setOnboardingStatus,
  }), [
    orgInfo,
    setOrgInfo,
    createPassword,
    setCreatePassword,
    verifyEmail,
    setVerifyEmail,
    fields,
    setFields,
    step,
    setStep,
    goBack,
    emailMasked,
    setEmailMasked,
    providerId,
    setProviderId,
    token,
    setToken,
    skipPassword,
    setSkipPassword,
    pendingOrgStep,
    setPendingOrgStep,
    orgInitialValues,
    setOrgInitialValues,
    totalSteps,
    handleSilentLogin,
    stepHistory,
    onboardingStatus,
    setOnboardingStatus,
  ]);

  return (
    <FreeTrialOnboardingContext.Provider value={contextValue}>
      {children}
    </FreeTrialOnboardingContext.Provider>
  );
}
